import Link from 'next/link'
import { SubjectCarousel, SectionTitle } from '@mc/educator-design-system'
import {
  TrackClick,
  nameToTrackingAction,
} from '../../../../tracking/educatorTracking'
import type { ProgramSubject } from '../../../../types/subjects'
import { paths } from '../../../../utils/paths'
import { SectionContainer } from '../../SectionContainer'

const SubjectProgramTile = ({ size }: { size: number }) => (
  <div
    className='relative shrink-0 overflow-hidden'
    style={{ width: size, height: size }}
  >
    <div className='absolute left-0 right-0 top-1/2 -translate-y-1/2 aspect-square bg-edu-subject-square-outer' />
    <div
      className='absolute top-1/2 -translate-y-1/2 aspect-square bg-edu-subject-square-mid'
      style={{ left: '20%', right: '20%' }}
    />
    <div
      className='absolute top-1/2 -translate-y-1/2 aspect-square bg-edu-subject-square-inner'
      style={{ left: '40%', right: '40%' }}
    />
  </div>
)

const ProgramSubjectCard = ({ subject }: { subject: ProgramSubject }) => {
  const cardClassName =
    'relative flex h-96 w-72 flex-col justify-start gap-4 overflow-hidden p-6 no-underline bg-edu-subject-bg'
  const cardContent = (
    <>
      <SubjectProgramTile size={96} />
      <h3 className='m-0 text-2xl font-normal leading-tight tracking-[-0.01em] text-white'>
        {subject.name}
      </h3>
    </>
  )

  if (subject.comingSoon) {
    return (
      <div
        data-subject={subject.tokenSlug}
        className={`${cardClassName} opacity-20 pointer-events-none cursor-default`}
      >
        {cardContent}
      </div>
    )
  }

  return (
    <TrackClick
      event='educatorSignClick'
      action={nameToTrackingAction(subject.name)}
      location='other subjects'
    >
      <Link
        data-subject={subject.tokenSlug}
        href={`${paths.subjects}/${subject.pageSlug}`}
        className={cardClassName}
      >
        {cardContent}
      </Link>
    </TrackClick>
  )
}

const SubjectListItem = ({ subject }: { subject: ProgramSubject }) => {
  const inner = (
    <>
      <SubjectProgramTile size={72} />
      <h3 className='m-0 text-xl font-normal capitalize leading-tight text-white'>
        {subject.name}
      </h3>
    </>
  )

  if (subject.comingSoon) {
    return (
      <div
        data-subject={subject.tokenSlug}
        className='flex items-center gap-4 bg-edu-bg-tertiary p-3 opacity-20 cursor-default'
      >
        {inner}
      </div>
    )
  }

  return (
    <TrackClick
      event='educatorSignClick'
      action={nameToTrackingAction(subject.name)}
      location='other subjects'
    >
      <Link
        data-subject={subject.tokenSlug}
        href={`${paths.subjects}/${subject.pageSlug}`}
        className='flex items-center gap-4 bg-edu-bg-tertiary p-3 no-underline'
      >
        {inner}
      </Link>
    </TrackClick>
  )
}

interface SubjectProgramSectionProps {
  sectionTitle: string
  subjects: ProgramSubject[]
}

export const SubjectProgramSection = ({
  sectionTitle,
  subjects,
}: SubjectProgramSectionProps) => {
  if (subjects.length === 0) return null

  return (
    <section
      data-section='subject-program'
      aria-label={sectionTitle}
      className='w-full overflow-hidden bg-black py-10'
    >
      <div className='md:hidden'>
        <SectionContainer>
          <SectionTitle title={sectionTitle} />
        </SectionContainer>
        <SectionContainer className='mt-6 flex flex-col gap-2.5'>
          {subjects.map((subject) => (
            <SubjectListItem key={subject.tokenSlug} subject={subject} />
          ))}
        </SectionContainer>
      </div>

      <div className='hidden md:block'>
        <SectionContainer clipX maxWidth={1152}>
          <SubjectCarousel
            title={sectionTitle}
            gap={16}
            scrollClassName='min-[1024px]:pl-[calc((100vw_-_982px)_/_2)] min-[1024px]:[scroll-padding-left:calc((100vw_-_982px)_/_2)] min-[1153px]:pl-24 min-[1153px]:[scroll-padding-left:96px]'
            ariaLabel={sectionTitle}
            prevLabel='Previous subject'
            nextLabel='Next subject'
          >
            {subjects.map((subject) => (
              <ProgramSubjectCard key={subject.tokenSlug} subject={subject} />
            ))}
          </SubjectCarousel>
        </SectionContainer>
      </div>
    </section>
  )
}
